import { ptBR } from '@mui/material/locale'
import { createTheme } from '@mui/material/styles'

const ProduttivoMuiTheme = createTheme(
    {
        palette: {
            background: {
                default: '#FFF',
                header: '#FAFAFA',
            },
            text: {
                primary: '#212121',
                secondary: '#616161',
                disabled: '#9E9E9E',
            },
            primary: {
                main: '#018923',
                dark: '#005A00',
                light: '#23A937',
                contrastText: '#FFFFFF',
                hover: '#0189230A',
                selected: '#01892314',
                focus: '#0189231F',
                focusVisible: '#0189234D',
                outlinedBorder: '#0189234D',
            },
            success: {
                main: '#007817',
                contrastText: '#FFF',
            },
            warning: {
                main: '#EF6C00',
            },
            info: {
                main: '#0288D1',
            },
            error: {
                main: '#C62828',
            },
            divider: '#0000001F',
        },
        spacing: 4,
        shape: {
            none: 0,
            borderRadius1: 4,
            borderRadius2: 8,
            borderRadius3: 16,
            borderRadius4: 24,
        },
        typography: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            h1: { fontSize: '6rem', fontWeight: 700, lineHeight: '116.7%', letterSpacing: '-0.09rem' },
            h2: { fontSize: '3.75rem', fontWeight: 700, lineHeight: '120%', letterSpacing: '-0.03rem' },
            h3: { fontSize: '3rem', fontWeight: 700, lineHeight: '116.7%' },
            h4: { fontSize: '2.125rem', fontWeight: 700, lineHeight: '123.5%', letterSpacing: '0.015rem' },
            h5: { fontSize: '1.5rem', fontWeight: 700, lineHeight: '133.4%' },
            h6: { fontSize: '1.25rem', fontWeight: 700, lineHeight: '160%', letterSpacing: '0.009rem' },
            subtitle1: { fontSize: '1rem', fontWeight: 400, lineHeight: '175%', letterSpacing: '0.009rem' },
            subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: '157%', letterSpacing: '0.006rem' },
            body1: { fontSize: '1rem', fontWeight: 400, lineHeight: '150%', letterSpacing: '0.009rem' },
            body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: '143%', letterSpacing: '0.010rem' },
            caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '166%', letterSpacing: '0.025rem' },
            overline: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '266%', letterSpacing: '0.0625rem' },
        },
        components: {
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        display: 'flex', justifyContent: 'center'
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        fontWeight: 500, '.MuiFormLabel-asterisk': { color: '#C62828' }
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: ({ ownerState }) => {
                        const customVariants = ['transparent', 'transparentErrorButton', 'outlined', 'cancelDialog', 'redOutlined', 'redButton']
                        const isDefaultFill = ownerState.color === 'primary' && !customVariants.includes(ownerState.variant)

                        return {
                            textTransform: 'none',
                            '&.MuiButton-outlinedSuccess': {
                                borderColor: '#01892380',
                            },
                            ...(isDefaultFill && {
                                color: '#FFF',
                                backgroundColor: '#018923',
                                '&:hover': {
                                    backgroundColor: '#01A32D',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#0000001F',
                                    color: '#00000061',
                                },
                            }),
                        }
                    },
                    sizeMedium: {
                        padding: '0.5rem 1.375rem',
                        borderRadius: '0.25rem',
                        minWidth: 'auto',
                        maxWidth: '16rem',
                        width: 'auto',
                    },
                    sizeLarge: {
                        padding: '0.5rem 1.375rem',
                        borderRadius: '0.25rem',
                        width: 'auto',
                        fontSize: '1.125rem',
                        '&.Mui-disabled': {
                            border: 'none',
                        },
                    },
                },
                variants: [
                    {
                        props: { variant: 'transparent' },
                        style: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                            },
                            '&:active': {
                                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                                boxShadow: 'none',
                            },
                        },
                    },
                    {
                        props: { variant: 'transparentErrorButton' },
                        style: {
                            backgroundColor: 'transparent',
                            color: '#5F2120',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                            },
                            '&:active': {
                                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                                boxShadow: 'none',
                            },
                        },
                    },
                    {
                        props: { variant: 'outlined' },
                        style: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            color: '#018923',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                            },
                            '&:active': {
                                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                                boxShadow: 'none',
                            },
                        },
                    },
                    {
                        props: { variant: 'cancelDialog' },
                        style: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            color: '#212121',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                            },
                            '&:active': {
                                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                                boxShadow: 'none',
                            },
                        },
                    },
                    {
                        props: { variant: 'redOutlined' },
                        style: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            color: '#C62828',
                            border: '1px solid #C62828',
                            borderRadius: '0.25rem',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                            },
                            '&:active': {
                                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                                boxShadow: 'none',
                            },
                            '&:disabled': {
                                backgroundColor: 'transparent',
                                border: '1px solid #9E9E9E',
                                color: '#9E9E9E'
                            },
                        },
                    },
                    {
                        props: { variant: 'redButton' },
                        style: {
                            backgroundColor: '#C62828',
                            boxShadow: 'none',
                            color: '#FFF',
                            '&:hover': {
                                backgroundColor: '#C62828',
                                boxShadow: 'none',
                            },
                            '&:active': {
                                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                                boxShadow: 'none',
                            },
                        },
                    },
                ],
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '12px',
                        lineHeight: '140%',
                    },
                },
            },
            MuiTablePagination: {
                styleOverrides: {
                    toolbar: {
                        alignItems: 'baseline',
                    },
                },
            },
            MuiChip: {
                variants: [
                    {
                        props: { color: 'success' },
                        style: {
                            color: '#FFF',
                            backgroundColor: '#007817',
                        },
                    },
                    {
                        props: { color: 'warning' },
                        style: {
                            color: '#FFF',
                            backgroundColor: '#EF6C00',
                        },
                    },
                    {
                        props: { color: 'error' },
                        style: {
                            color: '#FFF',
                            backgroundColor: '#C62828',
                        },
                    },
                    {
                        props: { color: 'info' },
                        style: {
                            color: '#FFF',
                            backgroundColor: '#0288D1',
                        },
                    },
                    {
                        props: { color: 'automatic' },
                        style: {
                            color: '#018923',
                            backgroundColor: '#2E7D3214',
                        },
                    },
                ],
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: '#0000008C'
                    }
                }
            },
            MuiFormHelperText: {
                variants: [
                    {
                        props: { variant: 'helperError' },
                        style: {
                            color: '#C62828',
                            fontWeight: 400,
                            fontSize: '0.75rem',
                            letterSpacing: '0.4px',
                            lineHeight: '166%',
                            marginTop: '0.25rem',
                        },
                    },
                ],
            },
        },
    },
    ptBR
)

export default ProduttivoMuiTheme
